import { useEffect, useState, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useDarkMode } from '../../../context/DarkModeContext'
import type {
  ProductType,
  Mode,
  Collaborator,
  ExternalLink,
  BookData,
  MerchData,
  EventData,
  BookBoxData,
  CreativeServiceData,
  ServiceProviderData,
  SaleData,
} from '../../../data/productManagerTypes'
import {
  mockUsers,
  mockBooks,
  mockMerch,
  mockEvents,
  mockBookBoxes,
  mockCreativeServices,
  mockServiceProviders,
} from '../../../data/productManagerData'
import type { BookEdition, Edition, EditionFile, RetailerLink } from './types'
import { checkAfterTideEligibility } from '../../../utils/aftertideEligibility'
import type {
  DarknessTier,
  ConsentFramework,
  ContentWarning,
} from '../../../data/aftertideTypes'

// Default form values
const defaultBookForm: Partial<BookData> = {
  collaborators: [],
  externalLinks: [],
  tags: [],
  contentWarnings: [],
  ageRating: 'all',
  relationshipType: '',
  themes: [],
  formats: {
    ebook: {
      available: false,
      price: 0,
    },
    audiobook: {
      available: false,
      price: 0,
    },
    print: {
      available: false,
      price: 0,
    },
  },
}
const defaultMerchForm: Partial<MerchData> = {
  images: [],
  tropes: [],
  linkedCreators: [],
  externalLinks: [],
  ageRating: 'all',
}
const defaultEventForm: Partial<EventData> = {
  relatedBooks: [],
  collaborators: [],
  externalLinks: [],
  ageRating: 'all',
  tags: [],
}
const defaultBookBoxForm: Partial<BookBoxData> = {
  includedItems: [],
  customizationOptions: [],
  tags: [],
  tropes: [],
  contentWarnings: [],
  collaborators: [],
  externalLinks: [],
  ageRating: 'all',
  themes: [],
  images: [],
}
const defaultCreativeForm: Partial<CreativeServiceData> = {
  specialties: [],
  processSteps: [],
  services: [],
  portfolioImages: [],
  willDo: [],
  wontDo: [],
  externalLinks: [],
  queueStatus: 'open',
  slotsAvailable: 3,
  maxSlots: 5,
}
const defaultServiceForm: Partial<ServiceProviderData> = {
  specialties: [],
  services: [],
  qualifications: [],
  willDo: [],
  wontDo: [],
  externalLinks: [],
  queueStatus: 'open',
  slotsAvailable: 3,
  maxSlots: 5,
  rateType: 'per-project',
  portfolioImages: [],
}
const defaultSaleForm: Partial<SaleData> = {
  discountType: 'percentage',
  dealType: 'self',
  productType: 'book',
}
export function useProductManager() {
  const { darkMode } = useDarkMode()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const itemDropdownRef = useRef<HTMLDivElement>(null)
  const collaboratorDropdownRef = useRef<HTMLDivElement>(null)
  const bookstoreDropdownRefs = useRef<{
    [key: number]: HTMLDivElement | null
  }>({})
  const bookstoreDropdownRefsMerch = useRef<{
    [key: number]: HTMLDivElement | null
  }>({})

  // Get initial values from URL params
  const initialType = searchParams.get('type') as ProductType | null
  const initialId = searchParams.get('id')
  const [productType, setProductType] = useState<ProductType | null>(
    initialType,
  )
  const [mode, setMode] = useState<Mode>(initialId ? 'edit' : 'create')
  const [selectedItemId, setSelectedItemId] = useState<string | null>(initialId)
  const [searchQuery, setSearchQuery] = useState('')
  const [showItemDropdown, setShowItemDropdown] = useState(false)

  // Confirmation state
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [savedAsDraft, setSavedAsDraft] = useState(false)
  const [showPublishConfirmModal, setShowPublishConfirmModal] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)

  // Edition state
  const [editions, setEditions] = useState<BookEdition[]>([])
  const [selectedEditionId, setSelectedEditionId] = useState<string | null>(
    null,
  )
  const [showEditionModal, setShowEditionModal] = useState(false)

  // New Editions Architecture State
  const [bookEditions, setBookEditions] = useState<Edition[]>([
    {
      id: Math.random().toString(36).substr(2, 9),
      formatType: 'ebook',
      isbn: '',
      price: '',
      comingSoon: false,
      publicationDate: '',
      retailers: [],
      coverVariant: null,
      files: [],
      isExpanded: true,
    },
  ])

  // Romance & Genre State
  const [selectedSubGenres, setSelectedSubGenres] = useState<string[]>([])
  const [secondaryGenres, setSecondaryGenres] = useState<string[]>([])
  const [subGenreSearch, setSubGenreSearch] = useState('')
  const [selectedSettings, setSelectedSettings] = useState<string[]>([])
  const [settingSearch, setSettingSearch] = useState('')
  const [povType, setPovType] = useState('')
  const [storyTense, setStoryTense] = useState('')
  const [romancePace, setRomancePace] = useState('')
  const [endingType, setEndingType] = useState('')
  const [connectionType, setConnectionType] = useState('')
  const [entryPoint, setEntryPoint] = useState('')
  const [seriesStatus, setSeriesStatus] = useState('')
  const [darknessLevel, setDarknessLevel] = useState(0)
  const [hasTaboo, setHasTaboo] = useState<boolean | null>(null)
  const [selectedTaboo, setSelectedTaboo] = useState<string[]>([])
  const [tabooInput, setTabooInput] = useState('')
  const [selectedRelTypes, setSelectedRelTypes] = useState<string[]>([])
  const [selectedDynamics, setSelectedDynamics] = useState<string[]>([])
  const [customDynamic, setCustomDynamic] = useState('')
  const [showAddDynamic, setShowAddDynamic] = useState(false)
  const [hasSpiceContent, setHasSpiceContent] = useState<boolean | null>(null)

  // Young Readers State
  const [recommendedAgeRange, setRecommendedAgeRange] = useState<
    'not-specified' | 'under-13' | '13-15' | '16-17' | 'adult-only'
  >('not-specified')
  const [selectedYRMoodTags, setSelectedYRMoodTags] = useState<string[]>([])
  const [selectedYRStoryTypes, setSelectedYRStoryTypes] = useState<string[]>([])

  // AfterTide State
  const [isAfterTideEligible, setIsAfterTideEligible] = useState(false)
  const [afterTideEligibilityReasons, setAfterTideEligibilityReasons] =
    useState<string[]>([])
  const [publishToAfterTide, setPublishToAfterTide] = useState(false)
  const [darknessTier, setDarknessTier] = useState<DarknessTier | null>(null)
  const [consentFramework, setConsentFramework] =
    useState<ConsentFramework | null>(null)
  const [atContentWarnings, setAtContentWarnings] = useState<ContentWarning[]>(
    [],
  )

  // Form states
  const [bookForm, setBookForm] = useState<Partial<BookData>>({
    ...defaultBookForm,
  })
  const [merchForm, setMerchForm] = useState<Partial<MerchData>>({
    ...defaultMerchForm,
  })
  const [eventForm, setEventForm] = useState<Partial<EventData>>({
    ...defaultEventForm,
  })
  const [bookBoxForm, setBookBoxForm] = useState<Partial<BookBoxData>>({
    ...defaultBookBoxForm,
  })
  const [creativeForm, setCreativeForm] = useState<
    Partial<CreativeServiceData>
  >({
    ...defaultCreativeForm,
  })
  const [serviceForm, setServiceForm] = useState<Partial<ServiceProviderData>>({
    ...defaultServiceForm,
  })
  const [saleForm, setSaleForm] = useState<Partial<SaleData>>({
    ...defaultSaleForm,
  })

  // Helper states
  const [tagInput, setTagInput] = useState('')
  const [warningInput, setWarningInput] = useState('')
  const [tropeInput, setTropeInput] = useState('')
  const [themeInput, setThemeInput] = useState('')
  const [subgenreInput, setSubgenreInput] = useState('')
  const [contentTagInput, setContentTagInput] = useState('')
  const [collaboratorSearch, setCollaboratorSearch] = useState('')
  const [showCollaboratorDropdown, setShowCollaboratorDropdown] =
    useState(false)
  const [selectedCollaboratorRole, setSelectedCollaboratorRole] =
    useState('Co-Writer')
  const [merchImageCount, setMerchImageCount] = useState(1)
  const [bookBoxImageCount, setBookBoxImageCount] = useState(1)
  const [bookstoreSearch, setBookstoreSearch] = useState('')
  const [showBookstoreDropdown, setShowBookstoreDropdown] = useState<
    number | null
  >(null)
  const [retailerSearches, setRetailerSearches] = useState<{
    [key: string]: string
  }>({})
  const [includedItemInput, setIncludedItemInput] = useState('')
  const [customizationInput, setCustomizationInput] = useState('')

  // Creative helper states
  const [specialtyInput, setSpecialtyInput] = useState('')
  const [willDoInput, setWillDoInput] = useState('')
  const [wontDoInput, setWontDoInput] = useState('')
  const [qualificationInput, setQualificationInput] = useState('')

  // File upload states for non-book types
  const [merchFiles, setMerchFiles] = useState<File[]>([])
  const [eventImageFile, setEventImageFile] = useState<File | null>(null)
  const [eventBannerFile, setEventBannerFile] = useState<File | null>(null)
  const [bookBoxFiles, setBookBoxFiles] = useState<File[]>([])
  const [coverFront, setCoverFront] = useState<File | null>(null)
  const [coverSpine, setCoverSpine] = useState<File | null>(null)
  const [coverBack, setCoverBack] = useState<File | null>(null)
  const [creativePortfolioFiles, setCreativePortfolioFiles] = useState<
    {
      file: File
      aspectRatio: 'portrait' | 'landscape' | 'square'
    }[]
  >([])
  const [servicePortfolioFiles, setServicePortfolioFiles] = useState<
    {
      file: File
      aspectRatio: 'portrait' | 'landscape' | 'square'
    }[]
  >([])

  // File input refs for edition covers
  const editionFileRefs = useRef<{
    [key: string]: HTMLInputElement | null
  }>({})

  // ---------- EFFECTS ----------

  // Click outside handler to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (
        showItemDropdown &&
        itemDropdownRef.current &&
        !itemDropdownRef.current.contains(target)
      ) {
        setShowItemDropdown(false)
      }
      if (
        showCollaboratorDropdown &&
        collaboratorDropdownRef.current &&
        !collaboratorDropdownRef.current.contains(target)
      ) {
        setShowCollaboratorDropdown(false)
      }
      if (showBookstoreDropdown !== null) {
        const dropdownRef =
          bookstoreDropdownRefs.current[showBookstoreDropdown] ||
          bookstoreDropdownRefsMerch.current[showBookstoreDropdown]
        if (dropdownRef && !dropdownRef.contains(target)) {
          setShowBookstoreDropdown(null)
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showItemDropdown, showCollaboratorDropdown, showBookstoreDropdown])

  // Load data when editing
  useEffect(() => {
    if (mode === 'edit' && selectedItemId) {
      if (productType === 'book') {
        const book = mockBooks.find((b) => b.id === selectedItemId)
        if (book) setBookForm(book)
      } else if (productType === 'merch') {
        const merch = mockMerch.find((m) => m.id === selectedItemId)
        if (merch) {
          setMerchForm(merch)
          setMerchImageCount(Math.max(1, merch.images.length))
        }
      } else if (productType === 'event') {
        const event = mockEvents.find((e) => e.id === selectedItemId)
        if (event) setEventForm(event)
      } else if (productType === 'bookbox') {
        const bookBox = mockBookBoxes.find((bb) => bb.id === selectedItemId)
        if (bookBox) setBookBoxForm(bookBox)
      } else if (productType === 'creative') {
        const creative = mockCreativeServices.find(
          (c) => c.id === selectedItemId,
        )
        if (creative) setCreativeForm(creative)
      } else if (productType === 'service') {
        const service = mockServiceProviders.find(
          (s) => s.id === selectedItemId,
        )
        if (service) setServiceForm(service)
      }
    }
  }, [mode, selectedItemId, productType])

  // Update URL when type/id changes
  useEffect(() => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      if (productType) params.set('type', productType)
      else params.delete('type')
      if (selectedItemId) params.set('id', selectedItemId)
      else params.delete('id')
      return params
    })
  }, [productType, selectedItemId, setSearchParams])

  // ---------- HELPER: Reset all forms ----------
  const resetAllForms = () => {
    setBookForm({
      ...defaultBookForm,
    })
    setMerchForm({
      ...defaultMerchForm,
    })
    setEventForm({
      ...defaultEventForm,
    })
    setBookBoxForm({
      ...defaultBookBoxForm,
    })
    setCreativeForm({
      ...defaultCreativeForm,
    })
    setServiceForm({
      ...defaultServiceForm,
    })
    setSaleForm({
      ...defaultSaleForm,
    })
  }

  // ---------- HANDLERS ----------

  const handleTypeSelect = (type: ProductType) => {
    setProductType(type)
    setSelectedItemId(null)
    setMode('create')
    setShowConfirmation(false)
    setShowPublishConfirmModal(false)
    setIsProcessing(false)
    resetAllForms()
  }
  const handleModeChange = (newMode: Mode) => {
    setMode(newMode)
    if (newMode === 'create') {
      setSelectedItemId(null)
      setShowConfirmation(false)
      setShowPublishConfirmModal(false)
      resetAllForms()
    }
  }
  const handleItemSelect = (id: string) => {
    setSelectedItemId(id)
    setShowItemDropdown(false)
  }
  const getExistingItems = () => {
    if (productType === 'book') return mockBooks
    if (productType === 'merch') return mockMerch
    if (productType === 'event') return mockEvents
    if (productType === 'bookbox') return mockBookBoxes
    if (productType === 'creative') return mockCreativeServices
    if (productType === 'service') return mockServiceProviders
    return []
  }
  const filteredItems = getExistingItems().filter((item) => {
    const title =
      'title' in item
        ? item.title
        : 'name' in item
          ? item.name
          : 'boxName' in item
            ? item.boxName
            : 'displayName' in item
              ? item.displayName
              : ''
    return title.toLowerCase().includes(searchQuery.toLowerCase())
  })

  // --- Content Tag Handlers ---
  const handleAddContentTag = (label: string, category: string = 'Trope') => {
    if (!label.trim()) return
    if (productType === 'book') {
      if (category === 'Warning') {
        if (!bookForm.contentWarnings?.includes(label)) {
          setBookForm((prev) => ({
            ...prev,
            contentWarnings: [...(prev.contentWarnings || []), label],
          }))
        }
      } else if (category === 'Relationship') {
        setBookForm((prev) => ({
          ...prev,
          relationshipType: label,
        }))
      } else if (category !== 'Theme') {
        if (!bookForm.tags?.includes(label)) {
          setBookForm((prev) => ({
            ...prev,
            tags: [...(prev.tags || []), label],
          }))
        }
      }
    } else if (productType === 'merch') {
      if (!merchForm.tropes?.includes(label)) {
        setMerchForm((prev) => ({
          ...prev,
          tropes: [...(prev.tropes || []), label],
        }))
      }
    } else if (productType === 'event') {
      if (!eventForm.tags?.includes(label)) {
        setEventForm((prev) => ({
          ...prev,
          tags: [...(prev.tags || []), label],
        }))
      }
    } else if (productType === 'bookbox') {
      if (category === 'Warning') {
        if (!bookBoxForm.contentWarnings?.includes(label)) {
          setBookBoxForm((prev) => ({
            ...prev,
            contentWarnings: [...(prev.contentWarnings || []), label],
          }))
        }
      } else if (category === 'Theme') {
        if (
          (bookBoxForm.themes?.length || 0) < 3 &&
          !bookBoxForm.themes?.includes(label)
        ) {
          setBookBoxForm((prev) => ({
            ...prev,
            themes: [...(prev.themes || []), label],
          }))
        }
      } else if (category === 'Setting' || category === 'Tag') {
        if (!bookBoxForm.tags?.includes(label)) {
          setBookBoxForm((prev) => ({
            ...prev,
            tags: [...(prev.tags || []), label],
          }))
        }
      } else {
        if (!bookBoxForm.tropes?.includes(label)) {
          setBookBoxForm((prev) => ({
            ...prev,
            tropes: [...(prev.tropes || []), label],
          }))
        }
      }
    }
    setContentTagInput('')
  }
  const handleRemoveContentTag = (label: string, category: string) => {
    if (productType === 'book') {
      if (category === 'Warning') {
        setBookForm((prev) => ({
          ...prev,
          contentWarnings: prev.contentWarnings?.filter((t) => t !== label),
        }))
      } else if (category === 'Relationship') {
        setBookForm((prev) => ({
          ...prev,
          relationshipType: '',
        }))
      } else {
        setBookForm((prev) => ({
          ...prev,
          tags: prev.tags?.filter((t) => t !== label),
        }))
      }
    } else if (productType === 'merch') {
      setMerchForm((prev) => ({
        ...prev,
        tropes: prev.tropes?.filter((t) => t !== label),
      }))
    } else if (productType === 'event') {
      setEventForm((prev) => ({
        ...prev,
        tags: prev.tags?.filter((t) => t !== label),
      }))
    } else if (productType === 'bookbox') {
      if (category === 'Warning') {
        setBookBoxForm((prev) => ({
          ...prev,
          contentWarnings: prev.contentWarnings?.filter((t) => t !== label),
        }))
      } else if (category === 'Theme') {
        setBookBoxForm((prev) => ({
          ...prev,
          themes: prev.themes?.filter((t) => t !== label),
        }))
      } else if (category === 'Setting' || category === 'Tag') {
        setBookBoxForm((prev) => ({
          ...prev,
          tags: prev.tags?.filter((t) => t !== label),
        }))
      } else {
        setBookBoxForm((prev) => ({
          ...prev,
          tropes: prev.tropes?.filter((t) => t !== label),
        }))
      }
    }
  }
  const getAllContentTags = () => {
    const tags: {
      label: string
      category: string
    }[] = []
    if (productType === 'book') {
      bookForm.tags?.forEach((t) =>
        tags.push({
          label: t,
          category: 'Trope',
        }),
      )
      bookForm.contentWarnings?.forEach((t) =>
        tags.push({
          label: t,
          category: 'Warning',
        }),
      )
      if (bookForm.relationshipType)
        tags.push({
          label: bookForm.relationshipType,
          category: 'Relationship',
        })
    } else if (productType === 'merch') {
      merchForm.tropes?.forEach((t) =>
        tags.push({
          label: t,
          category: 'Trope',
        }),
      )
    } else if (productType === 'event') {
      eventForm.tags?.forEach((t) =>
        tags.push({
          label: t,
          category: 'Tag',
        }),
      )
    } else if (productType === 'bookbox') {
      bookBoxForm.tropes?.forEach((t) =>
        tags.push({
          label: t,
          category: 'Trope',
        }),
      )
      bookBoxForm.tags?.forEach((t) =>
        tags.push({
          label: t,
          category: 'Setting',
        }),
      )
      bookBoxForm.contentWarnings?.forEach((t) =>
        tags.push({
          label: t,
          category: 'Warning',
        }),
      )
      bookBoxForm.themes?.forEach((t) =>
        tags.push({
          label: t,
          category: 'Theme',
        }),
      )
    }
    return tags
  }

  // --- Tag Handlers ---
  const handleAddTag = (
    type: 'book' | 'merch' | 'bookbox',
    input: string,
    setInput: (s: string) => void,
  ) => {
    if (input.trim()) {
      if (type === 'book') {
        if (!bookForm.tags?.includes(input.trim())) {
          setBookForm((prev) => ({
            ...prev,
            tags: [...(prev.tags || []), input.trim()],
          }))
        }
      } else if (type === 'merch') {
        if (!merchForm.tropes?.includes(input.trim())) {
          setMerchForm((prev) => ({
            ...prev,
            tropes: [...(prev.tropes || []), input.trim()],
          }))
        }
      } else if (type === 'bookbox') {
        if (!bookBoxForm.tropes?.includes(input.trim())) {
          setBookBoxForm((prev) => ({
            ...prev,
            tropes: [...(prev.tropes || []), input.trim()],
          }))
        }
      }
      setInput('')
    }
  }
  const handleRemoveTag = (
    type: 'book' | 'merch' | 'bookbox',
    index: number,
  ) => {
    if (type === 'book') {
      setBookForm((prev) => ({
        ...prev,
        tags: (prev.tags || []).filter((_, i) => i !== index),
      }))
    } else if (type === 'merch') {
      setMerchForm((prev) => ({
        ...prev,
        tropes: (prev.tropes || []).filter((_, i) => i !== index),
      }))
    } else if (type === 'bookbox') {
      setBookBoxForm((prev) => ({
        ...prev,
        tropes: (prev.tropes || []).filter((_, i) => i !== index),
      }))
    }
  }
  const handleAddWarning = (input?: string) => {
    const valueToAdd = input || warningInput
    if (valueToAdd.trim()) {
      if (productType === 'book') {
        if (!bookForm.contentWarnings?.includes(valueToAdd.trim())) {
          setBookForm((prev) => ({
            ...prev,
            contentWarnings: [
              ...(prev.contentWarnings || []),
              valueToAdd.trim(),
            ],
          }))
        }
      } else if (productType === 'bookbox') {
        if (!bookBoxForm.contentWarnings?.includes(valueToAdd.trim())) {
          setBookBoxForm((prev) => ({
            ...prev,
            contentWarnings: [
              ...(prev.contentWarnings || []),
              valueToAdd.trim(),
            ],
          }))
        }
      }
      setWarningInput('')
    }
  }
  const handleRemoveWarning = (index: number) => {
    if (productType === 'book') {
      setBookForm((prev) => ({
        ...prev,
        contentWarnings: (prev.contentWarnings || []).filter(
          (_, i) => i !== index,
        ),
      }))
    } else if (productType === 'bookbox') {
      setBookBoxForm((prev) => ({
        ...prev,
        contentWarnings: (prev.contentWarnings || []).filter(
          (_, i) => i !== index,
        ),
      }))
    }
  }
  const handleAddTheme = (input: string) => {
    if (input.trim()) {
      if (productType === 'bookbox') {
        if ((bookBoxForm.themes?.length || 0) < 3) {
          if (!bookBoxForm.themes?.includes(input.trim())) {
            setBookBoxForm((prev) => ({
              ...prev,
              themes: [...(prev.themes || []), input.trim()],
            }))
          }
        }
      } else {
        if ((bookForm.themes?.length || 0) < 3) {
          if (!bookForm.themes?.includes(input.trim())) {
            setBookForm((prev) => ({
              ...prev,
              themes: [...(prev.themes || []), input.trim()],
            }))
          }
        }
      }
      setThemeInput('')
    }
  }
  const handleRemoveRemoveTheme = (index: number) => {
    if (productType === 'bookbox') {
      setBookBoxForm((prev) => ({
        ...prev,
        themes: (prev.themes || []).filter((_, i) => i !== index),
      }))
    } else {
      setBookForm((prev) => ({
        ...prev,
        themes: (prev.themes || []).filter((_, i) => i !== index),
      }))
    }
  }

  // --- Collaborator Handlers ---
  const handleAddCollaborator = (user: (typeof mockUsers)[0], role: string) => {
    const newCollaborator: Collaborator = {
      id: user.id,
      name: user.name,
      role: role,
      avatar: user.avatar,
      verified: user.verified,
      status: user.verified ? 'verified' : 'pending',
    }
    if (productType === 'book') {
      setBookForm((prev) => ({
        ...prev,
        collaborators: [...(prev.collaborators || []), newCollaborator],
      }))
    } else if (productType === 'merch') {
      setMerchForm((prev) => ({
        ...prev,
        linkedCreators: [...(prev.linkedCreators || []), newCollaborator],
      }))
    } else if (productType === 'event') {
      setEventForm((prev) => ({
        ...prev,
        collaborators: [...(prev.collaborators || []), newCollaborator],
      }))
    } else if (productType === 'bookbox') {
      setBookBoxForm((prev) => ({
        ...prev,
        collaborators: [...(prev.collaborators || []), newCollaborator],
      }))
    }
    setCollaboratorSearch('')
  }
  const handleRemoveCollaborator = (index: number) => {
    if (productType === 'book') {
      setBookForm((prev) => ({
        ...prev,
        collaborators: (prev.collaborators || []).filter((_, i) => i !== index),
      }))
    } else if (productType === 'merch') {
      setMerchForm((prev) => ({
        ...prev,
        linkedCreators: (prev.linkedCreators || []).filter(
          (_, i) => i !== index,
        ),
      }))
    } else if (productType === 'event') {
      setEventForm((prev) => ({
        ...prev,
        collaborators: (prev.collaborators || []).filter((_, i) => i !== index),
      }))
    } else if (productType === 'bookbox') {
      setBookBoxForm((prev) => ({
        ...prev,
        collaborators: (prev.collaborators || []).filter((_, i) => i !== index),
      }))
    }
  }

  // --- External Link Handlers ---
  const handleAddLink = () => {
    const newLink: ExternalLink = {
      id: Math.random().toString(36).substr(2, 9),
      platform: '',
      url: '',
    }
    if (productType === 'book') {
      setBookForm((prev) => ({
        ...prev,
        externalLinks: [...(prev.externalLinks || []), newLink],
      }))
    } else if (productType === 'merch') {
      setMerchForm((prev) => ({
        ...prev,
        externalLinks: [...(prev.externalLinks || []), newLink],
      }))
    } else if (productType === 'event') {
      setEventForm((prev) => ({
        ...prev,
        externalLinks: [...(prev.externalLinks || []), newLink],
      }))
    } else if (productType === 'bookbox') {
      setBookBoxForm((prev) => ({
        ...prev,
        externalLinks: [...(prev.externalLinks || []), newLink],
      }))
    } else if (productType === 'service') {
      setServiceForm((prev) => ({
        ...prev,
        externalLinks: [...(prev.externalLinks || []), newLink],
      }))
    }
  }
  const handleSelectRetailer = (
    index: number,
    retailer: {
      id: string
      name: string
      isPlatform?: boolean
      platformUrl?: string
      location?: string
      verified?: boolean
      isCustom?: boolean
    },
  ) => {
    const updateLinks = (links: ExternalLink[] = []) => {
      const newLinks = [...links]
      newLinks[index] = {
        ...newLinks[index],
        platform: retailer.name,
        retailerId: retailer.id,
        isPlatform: retailer.isPlatform || false,
        platformUrl: retailer.platformUrl || '',
        url: retailer.isPlatform
          ? retailer.platformUrl || ''
          : newLinks[index].url,
      }
      return newLinks
    }
    if (productType === 'book') {
      setBookForm((prev) => ({
        ...prev,
        externalLinks: updateLinks(prev.externalLinks),
      }))
    } else if (productType === 'merch') {
      setMerchForm((prev) => ({
        ...prev,
        externalLinks: updateLinks(prev.externalLinks),
      }))
    } else if (productType === 'event') {
      setEventForm((prev) => ({
        ...prev,
        externalLinks: updateLinks(prev.externalLinks),
      }))
    } else if (productType === 'bookbox') {
      setBookBoxForm((prev) => ({
        ...prev,
        externalLinks: updateLinks(prev.externalLinks),
      }))
    } else if (productType === 'service') {
      setServiceForm((prev) => ({
        ...prev,
        externalLinks: updateLinks(prev.externalLinks),
      }))
    }
    setShowBookstoreDropdown(null)
    setRetailerSearches((prev) => ({
      ...prev,
      [`${productType}-${index}`]: '',
    }))
  }
  const handleUpdateLink = (
    index: number,
    field: keyof ExternalLink,
    value: string,
  ) => {
    const updateLinks = (links: ExternalLink[] = []) => {
      const newLinks = [...links]
      newLinks[index] = {
        ...newLinks[index],
        [field]: value,
      }
      return newLinks
    }
    if (productType === 'book') {
      setBookForm((prev) => ({
        ...prev,
        externalLinks: updateLinks(prev.externalLinks),
      }))
    } else if (productType === 'merch') {
      setMerchForm((prev) => ({
        ...prev,
        externalLinks: updateLinks(prev.externalLinks),
      }))
    } else if (productType === 'event') {
      setEventForm((prev) => ({
        ...prev,
        externalLinks: updateLinks(prev.externalLinks),
      }))
    } else if (productType === 'bookbox') {
      setBookBoxForm((prev) => ({
        ...prev,
        externalLinks: updateLinks(prev.externalLinks),
      }))
    } else if (productType === 'service') {
      setServiceForm((prev) => ({
        ...prev,
        externalLinks: updateLinks(prev.externalLinks),
      }))
    }
  }
  const handleRemoveLink = (index: number) => {
    if (productType === 'book') {
      setBookForm((prev) => ({
        ...prev,
        externalLinks: (prev.externalLinks || []).filter((_, i) => i !== index),
      }))
    } else if (productType === 'merch') {
      setMerchForm((prev) => ({
        ...prev,
        externalLinks: (prev.externalLinks || []).filter((_, i) => i !== index),
      }))
    } else if (productType === 'event') {
      setEventForm((prev) => ({
        ...prev,
        externalLinks: (prev.externalLinks || []).filter((_, i) => i !== index),
      }))
    } else if (productType === 'bookbox') {
      setBookBoxForm((prev) => ({
        ...prev,
        externalLinks: (prev.externalLinks || []).filter((_, i) => i !== index),
      }))
    } else if (productType === 'service') {
      setServiceForm((prev) => ({
        ...prev,
        externalLinks: (prev.externalLinks || []).filter((_, i) => i !== index),
      }))
    }
  }

  // --- Book Box Handlers ---
  const handleAddIncludedItem = () => {
    if (includedItemInput.trim()) {
      setBookBoxForm((prev) => ({
        ...prev,
        includedItems: [
          ...(prev.includedItems || []),
          includedItemInput.trim(),
        ],
      }))
      setIncludedItemInput('')
    }
  }
  const handleRemoveIncludedItem = (index: number) => {
    setBookBoxForm((prev) => ({
      ...prev,
      includedItems: (prev.includedItems || []).filter((_, i) => i !== index),
    }))
  }
  const handleAddCustomization = () => {
    if (customizationInput.trim()) {
      setBookBoxForm((prev) => ({
        ...prev,
        customizationOptions: [
          ...(prev.customizationOptions || []),
          customizationInput.trim(),
        ],
      }))
      setCustomizationInput('')
    }
  }
  const handleRemoveCustomization = (index: number) => {
    setBookBoxForm((prev) => ({
      ...prev,
      customizationOptions: (prev.customizationOptions || []).filter(
        (_, i) => i !== index,
      ),
    }))
  }

  // --- Creative Handlers ---
  const handleAddSpecialty = () => {
    if (specialtyInput.trim()) {
      setCreativeForm((prev) => ({
        ...prev,
        specialties: [...(prev.specialties || []), specialtyInput.trim()],
      }))
      setSpecialtyInput('')
    }
  }
  const handleRemoveSpecialty = (index: number) => {
    setCreativeForm((prev) => ({
      ...prev,
      specialties: (prev.specialties || []).filter((_, i) => i !== index),
    }))
  }
  const handleAddProcessStep = () => {
    setCreativeForm((prev) => ({
      ...prev,
      processSteps: [
        ...(prev.processSteps || []),
        {
          title: '',
          description: '',
        },
      ],
    }))
  }
  const handleUpdateProcessStep = (
    index: number,
    field: 'title' | 'description',
    value: string,
  ) => {
    setCreativeForm((prev) => {
      const newSteps = [...(prev.processSteps || [])]
      newSteps[index] = {
        ...newSteps[index],
        [field]: value,
      }
      return {
        ...prev,
        processSteps: newSteps,
      }
    })
  }
  const handleRemoveProcessStep = (index: number) => {
    setCreativeForm((prev) => ({
      ...prev,
      processSteps: (prev.processSteps || []).filter((_, i) => i !== index),
    }))
  }
  const handleAddService = () => {
    setCreativeForm((prev) => ({
      ...prev,
      services: [
        ...(prev.services || []),
        {
          id: Math.random().toString(36).substr(2, 9),
          name: '',
          price: '',
          description: '',
          features: [],
          turnaround: '',
          revisions: '',
        },
      ],
    }))
  }
  const handleUpdateService = (index: number, field: string, value: any) => {
    setCreativeForm((prev) => {
      const newServices = [...(prev.services || [])]
      newServices[index] = {
        ...newServices[index],
        [field]: value,
      }
      return {
        ...prev,
        services: newServices,
      }
    })
  }
  const handleRemoveService = (index: number) => {
    setCreativeForm((prev) => ({
      ...prev,
      services: (prev.services || []).filter((_, i) => i !== index),
    }))
  }
  const handleAddPortfolioImage = () => {
    setCreativeForm((prev) => ({
      ...prev,
      portfolioImages: [
        ...(prev.portfolioImages || []),
        {
          id: Math.random().toString(36).substr(2, 9),
          imageUrl: '',
          title: '',
          category: '',
          aspectRatio: 'portrait',
        },
      ],
    }))
  }
  const handleUpdatePortfolioImage = (
    index: number,
    field: string,
    value: any,
  ) => {
    setCreativeForm((prev) => {
      const newImages = [...(prev.portfolioImages || [])]
      newImages[index] = {
        ...newImages[index],
        [field]: value,
      }
      return {
        ...prev,
        portfolioImages: newImages,
      }
    })
  }
  const handleRemovePortfolioImage = (index: number) => {
    setCreativeForm((prev) => ({
      ...prev,
      portfolioImages: (prev.portfolioImages || []).filter(
        (_, i) => i !== index,
      ),
    }))
  }
  const handleAddBoundary = (type: 'will' | 'wont') => {
    const input = type === 'will' ? willDoInput : wontDoInput
    if (input.trim()) {
      setCreativeForm((prev) => ({
        ...prev,
        [type === 'will' ? 'willDo' : 'wontDo']: [
          ...(prev[type === 'will' ? 'willDo' : 'wontDo'] || []),
          input.trim(),
        ],
      }))
      if (type === 'will') setWillDoInput('')
      else setWontDoInput('')
    }
  }
  const handleRemoveBoundary = (type: 'will' | 'wont', index: number) => {
    setCreativeForm((prev) => ({
      ...prev,
      [type === 'will' ? 'willDo' : 'wontDo']: (
        prev[type === 'will' ? 'willDo' : 'wontDo'] || []
      ).filter((_, i) => i !== index),
    }))
  }

  // --- Service Provider Handlers ---
  const handleAddSpecialtyService = () => {
    if (specialtyInput.trim()) {
      setServiceForm((prev) => ({
        ...prev,
        specialties: [...(prev.specialties || []), specialtyInput.trim()],
      }))
      setSpecialtyInput('')
    }
  }
  const handleRemoveSpecialtyService = (index: number) => {
    setServiceForm((prev) => ({
      ...prev,
      specialties: (prev.specialties || []).filter((_, i) => i !== index),
    }))
  }
  const handleAddQualificationService = () => {
    if (qualificationInput.trim()) {
      setServiceForm((prev) => ({
        ...prev,
        qualifications: [
          ...(prev.qualifications || []),
          qualificationInput.trim(),
        ],
      }))
      setQualificationInput('')
    }
  }
  const handleRemoveQualificationService = (index: number) => {
    setServiceForm((prev) => ({
      ...prev,
      qualifications: (prev.qualifications || []).filter((_, i) => i !== index),
    }))
  }
  const handleAddServiceService = () => {
    setServiceForm((prev) => ({
      ...prev,
      services: [
        ...(prev.services || []),
        {
          id: Math.random().toString(36).substr(2, 9),
          name: '',
          price: '',
          description: '',
          features: [],
          turnaround: '',
          revisions: '',
          category: '',
        },
      ],
    }))
  }
  const handleUpdateServiceService = (
    index: number,
    field: string,
    value: any,
  ) => {
    setServiceForm((prev) => {
      const newServices = [...(prev.services || [])]
      newServices[index] = {
        ...newServices[index],
        [field]: value,
      }
      return {
        ...prev,
        services: newServices,
      }
    })
  }
  const handleRemoveServiceService = (index: number) => {
    setServiceForm((prev) => ({
      ...prev,
      services: (prev.services || []).filter((_, i) => i !== index),
    }))
  }
  const handleAddServicePortfolioImage = () => {
    setServiceForm((prev) => ({
      ...prev,
      portfolioImages: [
        ...(prev.portfolioImages || []),
        {
          id: Math.random().toString(36).substr(2, 9),
          imageUrl: '',
          title: '',
          category: '',
          aspectRatio: 'portrait',
        },
      ],
    }))
  }
  const handleUpdateServicePortfolioImage = (
    index: number,
    field: string,
    value: any,
  ) => {
    setServiceForm((prev) => {
      const newImages = [...(prev.portfolioImages || [])]
      newImages[index] = {
        ...newImages[index],
        [field]: value,
      }
      return {
        ...prev,
        portfolioImages: newImages,
      }
    })
  }
  const handleRemoveServicePortfolioImage = (index: number) => {
    setServiceForm((prev) => ({
      ...prev,
      portfolioImages: (prev.portfolioImages || []).filter(
        (_, i) => i !== index,
      ),
    }))
  }
  const handleAddLinkService = () => {
    const newLink: ExternalLink = {
      id: Math.random().toString(36).substr(2, 9),
      platform: '',
      url: '',
    }
    setServiceForm((prev) => ({
      ...prev,
      externalLinks: [...(prev.externalLinks || []), newLink],
    }))
  }
  const handleRemoveLinkService = (index: number) => {
    setServiceForm((prev) => ({
      ...prev,
      externalLinks: (prev.externalLinks || []).filter((_, i) => i !== index),
    }))
  }

  // --- New Editions Architecture Handlers ---
  const handleAddBookEdition = () => {
    setBookEditions((prev) => [
      ...prev,
      {
        id: Math.random().toString(36).substr(2, 9),
        formatType: 'paperback',
        isbn: '',
        price: '',
        comingSoon: false,
        publicationDate: '',
        retailers: [],
        coverVariant: null,
        files: [],
        isExpanded: true,
      },
    ])
  }

  const handleUpdateBookEdition = (
    id: string,
    field: keyof Edition,
    value: any,
  ) => {
    setBookEditions((prev) =>
      prev.map((ed) => (ed.id === id ? { ...ed, [field]: value } : ed)),
    )
  }

  const handleRemoveBookEdition = (id: string) => {
    setBookEditions((prev) => prev.filter((ed) => ed.id !== id))
  }

  const handleToggleEditionExpand = (id: string) => {
    setBookEditions((prev) =>
      prev.map((ed) =>
        ed.id === id ? { ...ed, isExpanded: !ed.isExpanded } : ed,
      ),
    )
  }

  const handleAddEditionFile = (
    editionId: string,
    file: File,
    accessLevel: EditionFile['accessLevel'],
    versionName: string,
  ) => {
    const newFile: EditionFile = {
      id: Math.random().toString(36).substr(2, 9),
      fileName: file.name,
      fileType: file.name.split('.').pop() || '',
      accessLevel,
      versionName: versionName || 'Final',
      uploadDate: new Date().toLocaleDateString(),
      size: file.size,
    }

    setBookEditions((prev) =>
      prev.map((ed) =>
        ed.id === editionId ? { ...ed, files: [...ed.files, newFile] } : ed,
      ),
    )
  }

  const handleUpdateEditionFile = (
    editionId: string,
    fileId: string,
    field: keyof EditionFile,
    value: any,
  ) => {
    setBookEditions((prev) =>
      prev.map((ed) => {
        if (ed.id !== editionId) return ed
        return {
          ...ed,
          files: ed.files.map((f) =>
            f.id === fileId ? { ...f, [field]: value } : f,
          ),
        }
      }),
    )
  }

  const handleRemoveEditionFile = (editionId: string, fileId: string) => {
    setBookEditions((prev) =>
      prev.map((ed) =>
        ed.id === editionId
          ? { ...ed, files: ed.files.filter((f) => f.id !== fileId) }
          : ed,
      ),
    )
  }

  const handleAddEditionRetailer = (editionId: string) => {
    const newRetailer: RetailerLink = {
      id: Math.random().toString(36).substr(2, 9),
      platform: '',
      url: '',
    }
    setBookEditions((prev) =>
      prev.map((ed) =>
        ed.id === editionId
          ? { ...ed, retailers: [...ed.retailers, newRetailer] }
          : ed,
      ),
    )
  }

  const handleUpdateEditionRetailer = (
    editionId: string,
    retailerId: string,
    field: keyof RetailerLink,
    value: string,
  ) => {
    setBookEditions((prev) =>
      prev.map((ed) => {
        if (ed.id !== editionId) return ed
        return {
          ...ed,
          retailers: ed.retailers.map((r) =>
            r.id === retailerId ? { ...r, [field]: value } : r,
          ),
        }
      }),
    )
  }

  const handleRemoveEditionRetailer = (
    editionId: string,
    retailerId: string,
  ) => {
    setBookEditions((prev) =>
      prev.map((ed) =>
        ed.id === editionId
          ? {
              ...ed,
              retailers: ed.retailers.filter((r) => r.id !== retailerId),
            }
          : ed,
      ),
    )
  }

  // --- Edition Helpers ---
  const addNewEdition = (format: BookEdition['format']) => {
    const newEdition: BookEdition = {
      id: Math.random().toString(36).substr(2, 9),
      format,
      images: {
        frontCover: null,
        spine: null,
        backCover: null,
      },
      metadata: {},
    }
    setEditions((prev) => [...prev, newEdition])
    setSelectedEditionId(newEdition.id)
  }
  const handleEditionFileChange = (
    editionId: string,
    imageType: keyof BookEdition['images'],
    file: File,
  ) => {
    setEditions((prev) =>
      prev.map((edition) => {
        if (edition.id === editionId) {
          return {
            ...edition,
            images: {
              ...edition.images,
              [imageType]: file,
            },
          }
        }
        return edition
      }),
    )
  }
  const updateEditionMetadata = (
    editionId: string,
    metadata: Partial<BookEdition['metadata']>,
  ) => {
    setEditions((prev) =>
      prev.map((edition) => {
        if (edition.id === editionId) {
          return {
            ...edition,
            metadata: {
              ...edition.metadata,
              ...metadata,
            },
          }
        }
        return edition
      }),
    )
  }
  const handleAddCustomDynamic = () => {
    if (customDynamic.trim() && !selectedDynamics.includes(customDynamic)) {
      setSelectedDynamics([...selectedDynamics, customDynamic])
      setCustomDynamic('')
      setShowAddDynamic(false)
    }
  }

  // --- Publish / Draft Handlers ---
  const handlePublishClick = () => {
    setShowPublishConfirmModal(true)
  }
  const handleConfirmPublish = () => {
    setIsProcessing(true)
    setTimeout(() => {
      setIsProcessing(false)
      setShowPublishConfirmModal(false)
      setShowConfirmation(true)
      setSavedAsDraft(false)
    }, 1500)
  }
  const handleSaveDraftClick = () => {
    setIsProcessing(true)
    setTimeout(() => {
      setIsProcessing(false)
      setShowConfirmation(true)
      setSavedAsDraft(true)
    }, 800)
  }

  // --- Young Readers Handlers & Computed ---
  const handleToggleYRMoodTag = (tag: string) => {
    setSelectedYRMoodTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    )
  }

  const handleToggleYRStoryType = (type: string) => {
    setSelectedYRStoryTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    )
  }

  const isYoungReaderEligible = (() => {
    const reasons: string[] = []
    let eligible = true

    if (hasSpiceContent) {
      eligible = false
      reasons.push('Contains spice content')
    }
    if (darknessLevel > 1) {
      eligible = false
      reasons.push('Darkness level is too high')
    }
    if (bookForm.contentWarnings && bookForm.contentWarnings.length > 0) {
      eligible = false
      reasons.push('Contains content warnings')
    }
    if (recommendedAgeRange === 'adult-only') {
      eligible = false
      reasons.push('Age range set to Adult Only')
    }

    return { eligible, reasons }
  })()

  // Check AfterTide eligibility whenever relevant fields change
  useEffect(() => {
    if (productType === 'book') {
      const eligibility = checkAfterTideEligibility({
        spiceLevel: hasSpiceContent ? 4 : 0, // Simplified for this example, would use actual spice level if available
        darknessLevel,
        genre: bookForm.genre,
        mainSubgenre: selectedSubGenres[0],
        selectedSubGenres,
        ageRating: bookForm.ageRating,
        contentWarnings: bookForm.contentWarnings,
      })

      setIsAfterTideEligible(eligibility.isEligible)
      setAfterTideEligibilityReasons(eligibility.reasons)

      // Auto-opt-out if no longer eligible
      if (!eligibility.isEligible && publishToAfterTide) {
        setPublishToAfterTide(false)
      }
    }
  }, [
    productType,
    hasSpiceContent,
    darknessLevel,
    bookForm.genre,
    selectedSubGenres,
    bookForm.ageRating,
    bookForm.contentWarnings,
  ])

  // Return everything needed by the components
  const pm = {
    darkMode,
    navigate,
    // Core state
    productType,
    setProductType,
    mode,
    setMode,
    selectedItemId,
    setSelectedItemId,
    searchQuery,
    setSearchQuery,
    showItemDropdown,
    setShowItemDropdown,
    // Confirmation
    showConfirmation,
    setShowConfirmation,
    savedAsDraft,
    setSavedAsDraft,
    showPublishConfirmModal,
    setShowPublishConfirmModal,
    isProcessing,
    setIsProcessing,
    // Editions
    editions,
    setEditions,
    selectedEditionId,
    setSelectedEditionId,
    showEditionModal,
    setShowEditionModal,
    // New Editions Architecture
    bookEditions,
    setBookEditions,
    handleAddBookEdition,
    handleUpdateBookEdition,
    handleRemoveBookEdition,
    handleToggleEditionExpand,
    handleAddEditionFile,
    handleUpdateEditionFile,
    handleRemoveEditionFile,
    handleAddEditionRetailer,
    handleUpdateEditionRetailer,
    handleRemoveEditionRetailer,
    // Romance & Genre
    selectedSubGenres,
    setSelectedSubGenres,
    secondaryGenres,
    setSecondaryGenres,
    subGenreSearch,
    setSubGenreSearch,
    selectedSettings,
    setSelectedSettings,
    settingSearch,
    setSettingSearch,
    povType,
    setPovType,
    storyTense,
    setStoryTense,
    romancePace,
    setRomancePace,
    endingType,
    setEndingType,
    connectionType,
    setConnectionType,
    entryPoint,
    setEntryPoint,
    seriesStatus,
    setSeriesStatus,
    darknessLevel,
    setDarknessLevel,
    hasTaboo,
    setHasTaboo,
    selectedTaboo,
    setSelectedTaboo,
    tabooInput,
    setTabooInput,
    selectedRelTypes,
    setSelectedRelTypes,
    selectedDynamics,
    setSelectedDynamics,
    customDynamic,
    setCustomDynamic,
    showAddDynamic,
    setShowAddDynamic,
    hasSpiceContent,
    setHasSpiceContent,
    // Young Readers State
    recommendedAgeRange,
    setRecommendedAgeRange,
    selectedYRMoodTags,
    setSelectedYRMoodTags,
    selectedYRStoryTypes,
    setSelectedYRStoryTypes,
    handleToggleYRMoodTag,
    handleToggleYRStoryType,
    isYoungReaderEligible,
    // AfterTide State
    isAfterTideEligible,
    afterTideEligibilityReasons,
    publishToAfterTide,
    setPublishToAfterTide,
    darknessTier,
    setDarknessTier,
    consentFramework,
    setConsentFramework,
    atContentWarnings,
    setAtContentWarnings,
    // Forms
    bookForm,
    setBookForm,
    merchForm,
    setMerchForm,
    eventForm,
    setEventForm,
    bookBoxForm,
    setBookBoxForm,
    creativeForm,
    setCreativeForm,
    serviceForm,
    setServiceForm,
    saleForm,
    setSaleForm,
    // Helper state
    tagInput,
    setTagInput,
    warningInput,
    setWarningInput,
    tropeInput,
    setTropeInput,
    themeInput,
    setThemeInput,
    subgenreInput,
    setSubgenreInput,
    contentTagInput,
    setContentTagInput,
    collaboratorSearch,
    setCollaboratorSearch,
    showCollaboratorDropdown,
    setShowCollaboratorDropdown,
    selectedCollaboratorRole,
    setSelectedCollaboratorRole,
    merchImageCount,
    setMerchImageCount,
    bookBoxImageCount,
    setBookBoxImageCount,
    bookstoreSearch,
    setBookstoreSearch,
    showBookstoreDropdown,
    setShowBookstoreDropdown,
    retailerSearches,
    setRetailerSearches,
    includedItemInput,
    setIncludedItemInput,
    customizationInput,
    setCustomizationInput,
    specialtyInput,
    setSpecialtyInput,
    willDoInput,
    setWillDoInput,
    wontDoInput,
    setWontDoInput,
    qualificationInput,
    setQualificationInput,
    // File states
    merchFiles,
    setMerchFiles,
    eventImageFile,
    setEventImageFile,
    eventBannerFile,
    setEventBannerFile,
    bookBoxFiles,
    setBookBoxFiles,
    coverFront,
    setCoverFront,
    coverSpine,
    setCoverSpine,
    coverBack,
    setCoverBack,
    creativePortfolioFiles,
    setCreativePortfolioFiles,
    servicePortfolioFiles,
    setServicePortfolioFiles,
    // Refs
    itemDropdownRef,
    collaboratorDropdownRef,
    bookstoreDropdownRefs,
    bookstoreDropdownRefsMerch,
    editionFileRefs,
    // Computed
    filteredItems,
    getExistingItems,
    // Handlers
    handleTypeSelect,
    handleModeChange,
    handleItemSelect,
    handleAddContentTag,
    handleRemoveContentTag,
    getAllContentTags,
    handleAddTag,
    handleRemoveTag,
    handleAddWarning,
    handleRemoveWarning,
    handleAddTheme,
    handleRemoveRemoveTheme,
    handleAddCollaborator,
    handleRemoveCollaborator,
    handleAddLink,
    handleSelectRetailer,
    handleUpdateLink,
    handleRemoveLink,
    handleAddIncludedItem,
    handleRemoveIncludedItem,
    handleAddCustomization,
    handleRemoveCustomization,
    handleAddSpecialty,
    handleRemoveSpecialty,
    handleAddProcessStep,
    handleUpdateProcessStep,
    handleRemoveProcessStep,
    handleAddService,
    handleUpdateService,
    handleRemoveService,
    handleAddPortfolioImage,
    handleUpdatePortfolioImage,
    handleRemovePortfolioImage,
    handleAddBoundary,
    handleRemoveBoundary,
    handleAddSpecialtyService,
    handleRemoveSpecialtyService,
    handleAddQualificationService,
    handleRemoveQualificationService,
    handleAddServiceService,
    handleUpdateServiceService,
    handleRemoveServiceService,
    handleAddServicePortfolioImage,
    handleUpdateServicePortfolioImage,
    handleRemoveServicePortfolioImage,
    handleAddLinkService,
    handleRemoveLinkService,
    addNewEdition,
    handleEditionFileChange,
    updateEditionMetadata,
    handleAddCustomDynamic,
    handlePublishClick,
    handleConfirmPublish,
    handleSaveDraftClick,
  }

  return pm
}

export type PM = ReturnType<typeof useProductManager>
